import { createSlice } from "@reduxjs/toolkit";
import {
  LOCAL_STORAGE_ITEMS_KEYS,
  PARKING_STATUSES,
  ROLES_NAMES,
  SCOPE_TYPE,
  STATES_NAMES,
  USER_TYPES,
  UTRON_PLARFORMS
} from "../../enums/Enums";
import * as encryptedLocalStorageActions from "../../managers/Storage/EncryptedLocalStorageActions";
import { getCurrentSites, getRole, getScopeByScopeId } from "../../utils/UsersUtils";

/**
 * Helper to merge selectionModel with updated user data
 * @param {Object} currentSelectionModel - Current selectionModel from state
 * @param {Object} updatedUser - Updated user/parker object
 * @param {Object} useServerExtendedData - Object indicating which extended fields to use from server
 */
const mergeSelectionModelData = (currentSelectionModel, updatedUser, useServerExtendedData = {}) => {
  const currentExtended = currentSelectionModel.utronUser;
  const updatedExtended = updatedUser.utronUser;
  
  return {
    ...updatedUser,
    utronUser: {
      ...updatedExtended,
      parkingHistories: useServerExtendedData.parkingHistories 
        ? updatedExtended?.parkingHistories 
        : (currentExtended?.parkingHistories || updatedExtended?.parkingHistories),
      invoiceSummaries: useServerExtendedData.invoiceSummaries 
        ? updatedExtended?.invoiceSummaries 
        : (currentExtended?.invoiceSummaries || updatedExtended?.invoiceSummaries),      
    }
  };
};

/**
 * Syncs selectionModel when extended data is refreshed from server
 * Uses server's extended data for all extended fields
 */
const syncSelectionModelWithExtendedDataRefresh = (state, updatedUser) => {
  if (!state.parkerSelectionModel || !updatedUser) return;
  
  const isMatching = 
    state.parkerSelectionModel?.identityId === updatedUser?.identityId ||
    state.parkerSelectionModel?.utronUser?.id === updatedUser?.utronUser?.id ||
    state.parkerSelectionModel?.id === updatedUser?.id;

  if (isMatching) {
    state.parkerSelectionModel = mergeSelectionModelData(
      state.parkerSelectionModel,
      updatedUser,
      { parkingHistories: true, invoiceSummaries: true, cars: true }
    );
  }
};

/**
 * Syncs selectionModel with updated user data, preserving extended data
 * Preserves currentSelectionModel's extended data if it exists
 */
const syncSelectionModelWithUpdatedUser = (state, updatedUser) => {
  if (!state.parkerSelectionModel || !updatedUser) return;
  
  const isMatching = 
    state.parkerSelectionModel?.identityId === updatedUser?.identityId ||
    state.parkerSelectionModel?.utronUser?.id === updatedUser?.utronUser?.id ||
    state.parkerSelectionModel?.id === updatedUser?.id;

  if (isMatching) {
    state.parkerSelectionModel = mergeSelectionModelData(
      state.parkerSelectionModel,
      updatedUser,
      {} // Empty object means preserve client extended data
    );
  }
};

/**
 * Processes a single parker with the same logic as setParkersAndGuests
 * Mutates the parker object with parking details, mainCarToken, userSiteActivation, cached images, and parking status
 * @param {Object} parker - Parker object to process
 * @param {Object} state - Redux state object
 */
const processSingleParker = (parker, state) => {
  let mainCarToken = null;  
  let parkingDeatils = parker.utronUser.parkingHistories.filter((parkingHistory) => {
    return state.selectedSites.some((site) => {
      return site.id === parkingHistory.siteId
    })
  });

  // added utronUser
  let carToken = parker.utronUser?.carUserTokens.find((token) => {
    mainCarToken = token?.carUserTokens?.find(
      (carToken) => carToken?.car?.carId === parkingDeatils.carId
    );
    return token.userTypeId !== USER_TYPES.TRANSIENT && mainCarToken;
  });

  //Set main car
  parker.mainCarToken = { ...carToken, ...mainCarToken };
  parker.mainCarToken.parkingDeatils = parkingDeatils;

  //Set user activation
  parker.userSiteActivation = parker.utronUser?.userSites.find(
    (userSite) => state.selectedSites.some(site => userSite.siteId === site.id)
  );

  // handle cached image - get the most updated image instead of using the cached one
  parker.image = `${parker?.image}?=${new Date().getTime()}`;

  if (parker.utronUser?.image !== undefined) {
    parker.utronUser.image = `${parker.utronUser?.image}?=${new Date().getTime()}`;
  }

  //Set user status
  if (!parker?.userSiteActivation?.isActive) {
    parker.mainCarToken.parkingStatus = PARKING_STATUSES.INACTIVE;
  } else {
    // with sort implementation
    parkingDeatils = parkingDeatils?.sort((a, b) => new Date(b.entranceDate).getTime() - new Date(a.entranceDate).getTime());
    parker.mainCarToken.parkingStatus = parkingDeatils[0]?.retrievalDate === null ? PARKING_STATUSES.IN : PARKING_STATUSES.OUT;
  }
};

const userInitialState = {
  isUserLoggedIn: JSON.parse(localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)) ? true : false,
  initialState: null,
  gmtUser: JSON.parse(localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)) : {},
  sites: [],
  scopes: [],
  allScopes: [],
  currentUsersSite: [],
  currentUsersScopes: [],
  currentUserTypes: [],
  guests: [],
  parkers: [],
  currentGmtUsers: [],
  currentChargeAccounts: [],
  selectedSites: [],
  selectedScope: null,
  parkerSelectionModel: null,
  showUserInfoWindow: false,
};

const userSlice = createSlice({
  name: STATES_NAMES.USER,
  initialState: userInitialState,
  reducers: {
    login(state, action) {
      state.isUserLoggedIn = localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION) ? true : false;
      state.gmtUser = action.payload.entity;
    },
    logout(state) {
      state.isUserLoggedIn = false;
      state.initialState = null;
      state.userIdentityId = "";
      state.gmtUser = {};
      state.sites = []; 
      state.scopes = [];
      state.allScopes = [];
      state.currentUsersSite = [];
      state.currentUsersScopes = [];
      state.currentUserTypes = [];
      state.guests = [];
      state.parkers = [];
      state.currentGmtUsers = [];
      state.currentChargeAccounts = [];
      state.selectedSites = [];
      state.selectedScope = null;
      state.parkerSelectionModel = null;
      state.showUserInfoWindow = false;
      encryptedLocalStorageActions.clearStorage();
      // encryptedLocalStorageActions.setStorageItem(
      //   LOCAL_STORAGE_ITEMS_KEYS.USER_PERSIST,
      //   ""
      // );
      // encryptedLocalStorageActions.setStorageItem(
      //   LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION,
      //   ""
      // );
    },
    setSites(state, action) {
      state.sites = action.payload;
      if (state.selectedSites.length < 1) {
        let scope = state.currentUsersScopes ? state.currentUsersScopes : state.scopes[0];
        state.selectedSites = getCurrentSites(state.sites, scope);
      }
    },
    setScopes(state, action) {
      state.scopes = action.payload;
      if (!state.selectedScope) {
        state.selectedScope = state?.scopes[0];
        let selectedScopes = [state.selectedScope];
        state.selectedSites = getCurrentSites(state.sites, selectedScopes);

      }
    },
    setAllScopes(state, action) {
      state.allScopes = action.payload;
    },
    setSelectedScope(state, action) {
      state.selectedScope = action.payload;
      encryptedLocalStorageActions.setStorageItem(
        LOCAL_STORAGE_ITEMS_KEYS.SELECTED_SCOPE_PERSIST,
        action.payload
      );
    },
    setInitialState(state, action) {
      state.initialState = action.payload;
      if (state.initialState.sites) {
        state.sites = state.initialState.sites;
      }
      state.scopes = state.initialState.scopes;
    },
    setShowUserInfoWindow(state, action) {
      state.showUserInfoWindow = action.payload;
    },

    setUsersSite(state, action) {
      //state.showUserInfoWindow = false;
      const selectedSiteId = action.payload;
      const site = state.sites?.find((site) => site.id === selectedSiteId);
      if (site) {
        state.selectedSite = site;
        const userScopeRole = getRoleByScopeId(state, site.scopeId);
        // const userScopeRole = state.initialState?.userScopesRoles?.find(
        //   (role) => role.scopeId == site.scopeId
        // )?.role;
        state.gmtUser.role = state.initialState?.roles?.find(
          (role) => role.id == userScopeRole?.id
        );
        if (site.userSites && site.userSites.length > 0) {
          state.currentUsersSite = site.userSites?.map((userSite) => {
            let user = userSite.user;
            user.carUserTokens = site.tokens.filter((token) => {
              return token.carUserTokens.some(
                (carToken) => carToken.userId === user.id
              );
            });
            return user;
          });
        } else {
          state.parkers = [];
          state.guests = [];
        }

        state.currentUserTypes = state.initialState.userTypes.filter(
          (userType) => {
            return !userType.siteId || userType.siteId === selectedSiteId;
          }
        );
      }
    },
    setUsersScope(state, action) {
      //state.showUserInfoWindow = false;
      const selectedScopeId = action.payload;
      const scope = state.scopes?.find((scope) => scope.id === selectedScopeId);
      if (scope) {
        state.selectedScope = scope;
        // let selectedSite = state.sites.find(site => site.scopeId === scope.id); 
        // if (selectedSite) {
        //   state.selectedSites = selectedSite;
        // }
        const userScopeRole = getRoleByScopeId(state, selectedScopeId);
        state.gmtUser.role = state.initialState?.roles?.find(
          (role) => role.id == userScopeRole?.id
        );
        if (state.currentUsersScopes && state.currentUsersScopes.length > 0) {
          // state.currentUsersScope = state.currentUsersScopes.map((userScope) => {
          //let userScope = state.currentUsersScopes.find()
          //let user = state.currentGmtUsers.find(user => user.identityId === userScope.userId);
          let sites = getCurrentSites(state.sites, state.currentUsersScopes);
          state.selectedSites = sites; //added
          let currentUsers = [];
          sites.map(site => {
            site.userSites?.map((userSite) => {
              let user = userSite.user;
              user.carUserTokens = site.tokens.filter((token) => {
                return token.carUserTokens.some(
                  (carToken) => carToken.userId === user.id
                );
              });
              currentUsers.push(user);
              return user;
            })
            site.tokens?.map((token) => {
              token.carUserTokens?.map((carUserToken) => {
                let user = carUserToken?.user;
                if (user !== null) { 
                  user.carUserTokens = site.tokens.filter((t) => {
                    return t.carUserTokens.some(
                      (carToken) => carToken.userId === user.id
                    );
                  });
                  if (user.id && !currentUsers.some((u) => u.id === carUserToken.userId)) {
                    currentUsers.push(user);
                  }
                }     
              });
            });
          });
          state.currentUsersSite = currentUsers;
          state.currentUserTypes = state.initialState.userTypes.filter(
            (userType) => {
              return !userType.siteId || sites.some(site => userType.siteId === site.Id);
            }
          );
        } else {
          state.parkers = [];
          state.guests = [];
          state.currentUserTypes = [];
        }
      }
    },
    setCurrentUsersScopes(state, action) {
      state.currentUsersScopes = action.payload
    },
    // addUserDataToParker(state, action) {
    //   const existingParker = state.currentUsersSite.find(
    //     (parker) => parker.identityId === action.payload.id
    //   );

    //   if (existingParker) {
    //     const existingParkerIndex =
    //       state.currentUsersSite.indexOf(existingParker);
    //     state.currentUsersSite[existingParkerIndex] = {
    //       ...existingParker,
    //       ...action.payload,
    //     };

    //     state.currentUsersSite[existingParkerIndex].app =
    //       state.currentUsersSite[existingParkerIndex].userToPlatforms.some(
    //         (platform) => platform.platformId === UTRON_PLARFORMS.MOBILE
    //       );
    //   }
    // },
    // Scopes
    addUserDataToParker(state, action) {
      const existingParker = state.currentUsersSite.find(
        (parker) => parker?.identityId === action.payload.id
      );

      if (existingParker) {
        const existingParkerIndex =
          state.currentUsersSite.indexOf(existingParker);
        state.currentUsersSite[existingParkerIndex] = {
          ...existingParker,
          ...action.payload,
        };

        // assigning utronUser to the flat obj for ui usage
        state.currentUsersSite[existingParkerIndex].firstName = state.currentUsersSite[existingParkerIndex].utronUser.firstName;
        state.currentUsersSite[existingParkerIndex].lastName = state.currentUsersSite[existingParkerIndex].utronUser.lastName;
        state.currentUsersSite[existingParkerIndex].image = state.currentUsersSite[existingParkerIndex].utronUser.image;

        state.currentUsersSite[existingParkerIndex].app =
          state.currentUsersSite[existingParkerIndex].userToPlatforms.some(
            (platform) => platform.platformId === UTRON_PLARFORMS.MOBILE
          );

        const updatedUser = state.currentUsersSite[existingParkerIndex];
        syncSelectionModelWithUpdatedUser(state, updatedUser);
      }
      ///////////
      // let sites = getCurrentSites(state.sites, state.currentUsersScopes);
      // let existingParker = null;
      // for(let site of sites) {
      //   state.currentUsersSite = [...state.currentUsersSite, site.userSites]; // {...formData, ["rates"]: ratesCopy}
      //   if (existingParker) {
      //     break;
      //   }
      //   existingParker = site.userSites.find(
      //     (parker) => parker.user.identityId === action.payload.id
      //   );
      // }

      // if (existingParker) {
      //   const existingParkerIndex =
      //     state.currentUsersSite.indexOf(existingParker);
      //   state.currentUsersSite[existingParkerIndex] = {
      //     ...existingParker,
      //     ...action.payload,
      //   };

      //   state.currentUsersSite[existingParkerIndex].app =
      //     state.currentUsersSite[existingParkerIndex].userToPlatforms.some(
      //       (platform) => platform.platformId === UTRON_PLARFORMS.MOBILE
      //     );
      // }
    },
    addMultipleUsersDataToParker(state, action) {
      const users = action.payload;
      if (!Array.isArray(users) || users.length === 0) return;
            
      const userMap = new Map();
      users.forEach(user => {
        if (user?.id) {
          userMap.set(user.id, user);
        }
      });
            
      const updatedIdentityIds = new Set();
      
      // Update all users in a single pass - matching the exact logic of addUserDataToParker
      // Important: find() only returns the FIRST match, so we only update the first parker per identityId
      state.currentUsersSite.forEach((parker, index) => {
        // Match the original logic: find parker by identityId === user.id
        if (parker?.identityId && !updatedIdentityIds.has(parker.identityId)) {
          const userData = userMap.get(parker.identityId);
          if (userData) {            
            const existingParker = parker;
            state.currentUsersSite[index] = {
              ...existingParker,
              ...userData,
            };
                        
            state.currentUsersSite[index].firstName = state.currentUsersSite[index].utronUser.firstName;
            state.currentUsersSite[index].lastName = state.currentUsersSite[index].utronUser.lastName;
            state.currentUsersSite[index].image = state.currentUsersSite[index].utronUser.image;
            
            
            state.currentUsersSite[index].app =
              state.currentUsersSite[index].userToPlatforms.some(
                (platform) => platform.platformId === UTRON_PLARFORMS.MOBILE
              );            
            
            const updatedUser = state.currentUsersSite[index];
            syncSelectionModelWithUpdatedUser(state, updatedUser);
            updatedIdentityIds.add(parker.identityId);
          }
        }
      });
    },
    deleteParker(state, action) {
      const userId = action.payload;
      state.currentUsersSite = state.currentUsersSite.filter((parker) => parker?.utronUser?.id !== userId);
      state.currentGmtUsers = state.currentGmtUsers.filter(user => user.utronUser?.id !== userId);
    },
    deleteChargeAccount(state, action) {
      const userId = action.payload;
      state.currentChargeAccounts = state.currentChargeAccounts.filter((acc) => acc?.utronUser?.id !== userId);
    },
    setParkersAndGuests(state, action) {
      // when setAllParkers is true - sets all parkers with no permissions limitations (by checking userScopes hierarchies), 
      // default is false
      const setAllParkers = action.payload?.setAllParkers || false;
      const customerRole = getRole(state.initialState.roles, ROLES_NAMES.CUSTOMER);
      state.guests = [];

      if (setAllParkers) {
        state.parkers = state.currentUsersSite.filter((parker) => {
          return parker?.utronUser?.userToUserTypes?.find(
            (userType) => userType.userType.id !== USER_TYPES.TRANSIENT
          );
        });
      }
      else {
        // (default case) - limits that when scope project selected on parkers / admin area - connected admin can't see another admin on table
        state.parkers = state.currentUsersSite.filter((parker) => {
          // indicates if logged in user is the parker
          let isParkerLoggedIn = state.gmtUser?.utronUser?.identityId === parker?.utronUser?.identityId;
          let isUserHasHigherUserScope = parker?.userScopes?.some(us => {         
            let foundedScope = getScopeByScopeId(state.allScopes, us.scopeId);
  
            // if parker has any higher level scope of userScope than the current selectedScope
            // and if user created from app (Global scope & customer role - add to list)
            return state.selectedScope?.scopeLevel > foundedScope?.scopeLevel && us.roleId !== customerRole.id;
          });
  
          // if user doesnt have any higher user scopes than selectedScope level, (and also not transient)
          // then add parker to parker list
          // isParkerLoggedIn - show logged in user on parkers list even if he has admin in utron global scope
          // (in order that logged in user can see himself)
          return (!isUserHasHigherUserScope || isParkerLoggedIn) && parker?.utronUser?.userToUserTypes?.find(
            (userType) => userType.userType.id !== USER_TYPES.TRANSIENT
          );
        });
      }

      const guests = state.currentUsersSite;

      guests.forEach((guest) => {

        // attach utronUser, if utronUser doesnt exist
        if (guest?.utronUser === undefined) {
          let utronUser = state.parkers.find(parker => parker.identityId === guest.identityId)?.utronUser;
          guest = {...guest, utronUser};
        }

        // attach phoneNumber, if phoneNumber doesnt exist
        if (guest?.phoneNumber === undefined) {
          let phoneNumber = state.parkers.find(parker => parker.identityId === guest.identityId)?.phoneNumber;
          guest = {...guest, phoneNumber};
        }

        guest.carUserTokens.forEach((token, index) => {
          if (token.userTypeId === USER_TYPES.TRANSIENT) {
            let carToken = guest.utronUser?.carUserTokens.find(
              (carToken) => carToken.userId === guest?.utronUser?.id
            )

            let parkingDeatils = guest.utronUser?.parkingHistories.filter((parkingHistory) => {
              return state.selectedSites.some((site) => {
                if ( site.id === parkingHistory.siteId) {
                  parkingHistory.site = site;
                  return true;
                }
                return false;
              }) && (token.id === parkingHistory.tokenId)
            });           
              
            // find mainCarToken car according to the carUserToken
            let car = guest.utronUser?.cars?.find(car => car?.id === token?.carUserTokens[0]?.carId);
            
            // added - parkingDeatils[0]?.sort
            parkingDeatils = parkingDeatils?.sort((a, b) => new Date(b.entranceDate).getTime() - new Date(a.entranceDate).getTime());
            parkingDeatils = parkingDeatils ? parkingDeatils[0] : parkingDeatils;
            
            // finds the invoiceSummary by parkingDetails - used to show the final price amount
            let invoiceSummary = guest.utronUser?.invoiceSummaries?.find(invoiceSummary => invoiceSummary?.parkingHistoryId === parkingDeatils?.id);

            // set the latest single parkingDetail to the guest 
            let mainCarToken = { ...token, ...carToken, car, invoiceSummary, parkingDeatils};
            
            // handle cached image - get the most updated image instead of using the cached one
            guest.image = `${guest.image}?=${new Date().getTime()}`;
            
            if (guest.utronUser?.image !== undefined) {
              guest.utronUser.image = `${guest.utronUser?.image}?=${new Date().getTime()}`;
            }

            //Set user activation // TODO: Ask daniel to show multiple site activation
            guest.userSiteActivation = guest.utronUser?.carUserTokens.find(
              (carUserToken) => state.selectedSites.some(site => site.id === carUserToken.token.siteId)
            );

            //Set user status
            let parkingStatus = null;
            if (!parkingDeatils) {
              parkingStatus = PARKING_STATUSES.INACTIVE;
            } else {
              // PREV CODE
              // parkingStatus =
              //   guest.mainCarToken?.parkingDeatils?.filter(
              //     (details) => !details?.retrievalDate
              //   )?.length > 0
              //     ? PARKING_STATUSES.IN
              //     : PARKING_STATUSES.OUT;
              parkingStatus = parkingDeatils?.retrievalDate === null ? PARKING_STATUSES.IN : PARKING_STATUSES.OUT;
            }

            mainCarToken = { ...mainCarToken, parkingStatus };
            let newGuest = { ...guest, mainCarToken };
            newGuest = { ...newGuest, id: index };

            //tempGuests.push({ ...newGuest });
            state.guests.push({ ...newGuest });
          }
        });
      });

      state.parkers.forEach((parker, index) => {
        processSingleParker(parker, state);
      });

      if (state.parkerSelectionModel) {
        const matchingParker = state.parkers.find(
          parker => parker.identityId === state.parkerSelectionModel?.identityId
        );
        
        if (matchingParker) {
          syncSelectionModelWithUpdatedUser(state, matchingParker);
        }
      }
    },
    setUserCarTokens(state, action) {
      //state.showUserInfoWindow = false;
      const userId = action.payload?.userId;
      const cars = action.payload?.cars;
      const carUserTokens = action.payload?.carTokens;
      const existingParker = state.currentUsersSite.find(
        (parker) => parker?.utronUser?.id === userId
      );

      if (existingParker) {
        const existingParkerIndex =
          state.currentUsersSite.indexOf(existingParker);
        state.currentUsersSite[existingParkerIndex].carUserTokens.map(
          (token, index) => {
            let updatedCarToken = carUserTokens.filter(
              (carUserToken) => carUserToken.tokenId === token.id
            );
            state.currentUsersSite[existingParkerIndex].carUserTokens[
              index
            ].carUserTokens = updatedCarToken;
          }
        );
        state.currentUsersSite[existingParkerIndex].cars = cars;
        let utronUser = state.currentUsersSite[existingParkerIndex].utronUser;
        if (utronUser && utronUser?.cars) {
          utronUser.cars = cars;
        }
      }
    },
    resetTablesData(state) {
      state.guests = [];
      state.parkers = [];
    },
    setCurrentGmtUsers(state, action) {
      state.currentGmtUsers = action.payload;
    },
    setCurrentChargeAccounts(state, action) {
      state.currentChargeAccounts = action.payload;
    },
    updateGmtUser(state, action) {
      // checks if logged user updates his own info - then update logged in user info and auth key
      if (
        state.gmtUser?.userIdentity?.id ===
        action.payload.entity?.userIdentity?.id
        ) {
        // handles the logged in user info (gmtUser)
        state.gmtUser.userIdentity = action.payload.entity?.userIdentity;
        state.gmtUser.utronUser = action.payload.entity?.utronUser;

        // handle cached image - get the most updated image instead of using the cached one
        state.gmtUser.utronUser.image =  `${state.gmtUser.utronUser.image}?=${new Date().getTime()}`;

        // after user updated his own info - handles auth key to deal with most updated data for the next refresh
        let localGmtUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION)) : {};
        let authToUpdate = {...action?.payload?.entity};

        // arrange object - set missing objects (userSites / userPlatforms) to entity from current gmtUser info
        authToUpdate.accessToken = localGmtUser.accessToken;
        authToUpdate.userIdentity.userToPlatforms = localGmtUser.userIdentity.userToPlatforms;
        authToUpdate.utronUser.userSites = localGmtUser.utronUser.userSites;

        // set localStorage to app auth key
        localStorage.setItem(LOCAL_STORAGE_ITEMS_KEYS.AUTHENTICATION, JSON.stringify(authToUpdate));
      }
    },
    setUpdatedGmtUser(state, action) {
      // handles the selected user to update in the Admin users area - for ui
      let updatedUser = action.payload.entity;
      let userToUpdate = state.currentGmtUsers.find((user) => {
        return user?.id === action.payload.entity?.userIdentity?.id
      });
      if (userToUpdate && updatedUser) {
        userToUpdate.utronUser.firstName = updatedUser.utronUser?.firstName;
        userToUpdate.utronUser.lastName = updatedUser.utronUser?.lastName; 
        userToUpdate.utronUser.phoneNumber = updatedUser.userIdentity?.phoneNumber;
        userToUpdate.utronUser.email = updatedUser.userIdentity?.email;
        userToUpdate.utronUser.image = `${updatedUser.utronUser?.image}?=${new Date().getTime()}`; // // handle cached image - get the most updated image instead of using the cached one
        userToUpdate.utronUser.userAdditionalData[0] = updatedUser.utronUser?.userAdditionalData[0];
      }
    },
    setUpdatedChargeAccount(state, action) {
      let updatedAcc = action.payload.entity;
      let accToUpdate = state.currentChargeAccounts.find((acc) => {
        return acc?.utronUser?.id === updatedAcc.utronUser.id
      });
      if (accToUpdate && updatedAcc) {
        accToUpdate.utronUser.chargeAccount.name = updatedAcc.utronUser?.chargeAccount?.name;
        accToUpdate.utronUser.firstName = updatedAcc.utronUser?.firstName;
        accToUpdate.utronUser.lastName = updatedAcc.utronUser?.lastName;
        accToUpdate.phoneNumber = updatedAcc.userIdentity?.phoneNumber;
        accToUpdate.email = updatedAcc.userIdentity?.email;
        accToUpdate.utronUser.userAdditionalData[0] = updatedAcc.utronUser?.userAdditionalData[0];     
      }
    },
    updateGmtUserScope(state, action) {
      const gmtUserToUpdate = state.currentGmtUsers.find(
        (gmtUser) => gmtUser.id === action.payload.entity.userId
      );
      if (gmtUserToUpdate) {
        const existingGmtUserIndex =
          state.currentGmtUsers.indexOf(gmtUserToUpdate);
        const exsistUserScope = gmtUserToUpdate.userScopes.find(
          (userScope) => userScope.scopeId === action.payload.entity.scopeId
        );
        if (exsistUserScope) {
          const exsistUserScopeIndex = gmtUserToUpdate.userScopes.indexOf(exsistUserScope);
          state.currentGmtUsers[existingGmtUserIndex].userScopes[
            exsistUserScopeIndex
          ].roleId = action.payload.entity.roleId;
          state.currentGmtUsers[existingGmtUserIndex].userScopes[
            exsistUserScopeIndex
          ].role = state.initialState?.roles?.find(
            (role) => role.id == action.payload.entity.roleId
          );
        } else {
          let userScopeToAdd = {
            ...action.payload.entity,
            role: state.initialState?.roles?.find(
              (role) => role.id === action.payload.entity.roleId),
            scope: state.initialState?.scopes?.find(
              (scope) => scope.id === action.payload.entity.scopeId)
          };

          state.currentGmtUsers[existingGmtUserIndex]?.userScopes?.push(userScopeToAdd);
        }
      }

      if (state.gmtUser?.userIdentity?.id === action.payload.entity.userId && state.selectedScope.id === action.payload.entity.scopeId) {
        let role = state.initialState?.roles?.find(
          (role) => role.id == gmtUserToUpdate?.roleId
        );
        if (role) {
          state.gmtUser.role = role;
        }
      }
    },
    deleteGmtUserScopes(state, action) {
      let userId = action.payload?.entity[0]?.userId;
      const gmtUserToUpdate = state.currentGmtUsers.find(
        (gmtUser) => gmtUser.id === userId
      );

      if (gmtUserToUpdate && userId) {
        const existingGmtUserIndex =
          state.currentGmtUsers.indexOf(gmtUserToUpdate);
        let updatedUserScopes = state.currentGmtUsers[existingGmtUserIndex].userScopes?.filter(us => action.payload.entity.some(newUs => newUs.scopeId === us.scopeId));
        if (updatedUserScopes) {
          state.currentGmtUsers[existingGmtUserIndex].userScopes = updatedUserScopes;
        }
      }
    },
    setUpdatedParker(state, action) {
      // handles the selected parker user to update in the Parkers area - for ui
      let updatedUser = action.payload;
      let userToUpdate = state.parkers.find(user => user?.id === updatedUser?.userIdentity?.id);
      if (userToUpdate && updatedUser) {
        userToUpdate.firstName = updatedUser.utronUser.firstName;
        userToUpdate.lastName = updatedUser.utronUser.lastName;
        userToUpdate.phoneNumber = updatedUser.userIdentity.phoneNumber;
        userToUpdate.email = updatedUser.userIdentity.email;
        userToUpdate.image = `${updatedUser.utronUser.image}?=${new Date().getTime()}`; // handle cached image - get the most updated image instead of using the cached one // updatedUser.utronUser.image
        userToUpdate.utronUser.userAdditionalData[0] = updatedUser.utronUser.userAdditionalData[0];
        
        syncSelectionModelWithUpdatedUser(state, userToUpdate);
      }
    },
    updateUserExtendedData(state, action) {
      const userId = action.payload?.userId;
      const extendedData = action.payload?.extendedData;

      if (!userId || !extendedData) {
        return;
      }
      
      const existingParker = state.currentUsersSite.find(
        (parker) => parker?.utronUser?.id === userId
      );

      if (existingParker && existingParker.utronUser) {
        const existingParkerIndex = state.currentUsersSite.indexOf(existingParker);
        
        // Merge extended data into existing utronUser
        state.currentUsersSite[existingParkerIndex].utronUser = {...existingParker.utronUser, ...extendedData};
        
        const updatedUser = state.currentUsersSite[existingParkerIndex];
        syncSelectionModelWithExtendedDataRefresh(state, updatedUser);

        // Update parker if exists
        const parkerIndex = state.parkers.findIndex(
          parker => parker?.utronUser?.id === userId
        );

        if (parkerIndex !== -1) {
          const updatedUtronUser = {...state.parkers[parkerIndex].utronUser, ...extendedData};
          if (updatedUtronUser) {
            state.parkers[parkerIndex].utronUser = updatedUtronUser;
          }
        }

        // Update guest if exists
        const guestIndex = state.guests.findIndex(
          guest => guest?.utronUser?.id === userId
        );
        if (guestIndex !== -1) {
          const updatedUtronUser = {...state.guests[guestIndex].utronUser, ...extendedData};
          if (updatedUtronUser) {
            state.guests[guestIndex].utronUser = updatedUtronUser;
          }
        }
      }
    },
    processSingleParkerInState(state, action) {      
      const userId = action.payload?.userId;
      if (!userId) return;
      
      // Get the updated user from currentUsersSite (has fresh API response data)
      const updatedUser = state.currentUsersSite.find(
        user => user?.utronUser?.id === userId
      );
      
      if (!updatedUser) return;
      
      // Update parker if exists in parkers array
      const parkerIndex = state.parkers.findIndex(
        parker => parker?.utronUser?.id === userId
      );
      
      if (parkerIndex !== -1) {
        // Merge all updated user data from currentUsersSite into the parker
        // This ensures processSingleParker receives the fresh API response data
        state.parkers[parkerIndex] = {
          ...state.parkers[parkerIndex],
          ...updatedUser,
          utronUser: {
            ...state.parkers[parkerIndex].utronUser,
            ...updatedUser.utronUser,
          }
        };
        
        // Apply full processing logic with the updated data
        processSingleParker(state.parkers[parkerIndex], state);
        
        // Sync selection model if matching
        if (state.parkerSelectionModel) {
          const isMatching = 
            state.parkerSelectionModel?.identityId === state.parkers[parkerIndex]?.identityId ||
            state.parkerSelectionModel?.utronUser?.id === state.parkers[parkerIndex]?.utronUser?.id ||
            state.parkerSelectionModel?.id === state.parkers[parkerIndex]?.id;
                    
          if (isMatching) {            
            syncSelectionModelWithUpdatedUser(state, state.parkers[parkerIndex]);
          }
        }
      }
    },
    setParkerSelectionModel(state, action) {
      state.parkerSelectionModel = action.payload;
    }
  }
});

const getRoleByScopeId = (state, scopeId) => {
  // global - once user has admin for global on any of its userScopes return its admin role 
  // which allows for permissions / areas
  let userScopeRole = null;
  let adminRole = getRole(state.initialState?.roles, ROLES_NAMES.ADMINISTRATOR);
  userScopeRole = state.initialState?.userScopesRoles?.find(us => {
    let foundedScope = getScopeByScopeId(state.allScopes, us?.scopeId);
    if (foundedScope?.type === SCOPE_TYPE.GLOBAL && us?.roleId === adminRole?.id) {
      return us?.role; // return the user's founded userScope's admin global role
    }
  })?.role;

  // if global admin role not founded, check on selected scope
  if (!userScopeRole) {
    userScopeRole = state.initialState?.userScopesRoles?.find(
      (role) => role.scopeId == scopeId
    )?.role;

    // while (!userScopeRole) {
    //   let currentScope = state.initialState?.scopes?.find((s) => s.id == scopeId)
    //   let parentScopeId = currentScope.parentId;
    //   scopeId = parentScopeId;
    //   userScopeRole = state.initialState?.userScopesRoles?.find(
    //     (role) => role.scopeId == scopeId
    //   )?.role;
    // }
  }

  return userScopeRole;
};


export const userActions = userSlice.actions;

export default userSlice.reducer;
